import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import '../../App.css';
import './MangaPage.css';
import '../anime/AnimePage.css';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import PageShell from '../../components/layout/PageShell';
import useScrolled from '../../hooks/useScrolled';
import MangaApiService from '../../services/MangaApiService';
import ApiService from '../../services/ApiService';
import ExternalLinkService from '../../services/ExternalLinkService';
import MangaAccessNotice from './MangaAccessNotice';
import MangaCardComponent from '../../components/mangacard/MangaCardComponent';
import ProfilePic from '../../components/profilepic/ProfilePic';
import { getTeamId,
    resolveDonationLinks,
    translateStatus,
    unwrapList,
    unwrapResponse,
    getMangaErrorMessage,
    translateTeamRole,
} from './mangaStudioUtils';
import teamIcon from '../../assets/ic_profile.svg';
import backIcon from '../../assets/ic_arrow_right.svg';

const API_DOMAIN = 'https://api.whitewhale.help';
const PAGE_SIZE = 20;
const SCROLL_BUFFER_PX = 400;

const getAvatarUrl = (accountId) => (
    accountId ? `${API_DOMAIN}/account/getProfileData/${accountId}_profile_pic.png` : null
);

const profileByNickCache = new Map();
const profileByNickInFlight = new Map();

const fetchProfileByNick = async (nick) => {
    if (!nick) {
        return null;
    }
    const raw = String(nick).trim();
    const key = raw.toLowerCase();
    if (!key || !raw) {
        return null;
    }
    if (profileByNickCache.has(key)) {
        return profileByNickCache.get(key);
    }
    if (profileByNickInFlight.has(key)) {
        return profileByNickInFlight.get(key);
    }
    const request = ApiService.get(raw)
        .then((response) => {
            const payload = response?.data || response;
            const profile = payload?.data || payload;
            if (profile) {
                profileByNickCache.set(key, profile);
            }
            return profile || null;
        })
        .catch(() => null)
        .finally(() => {
            profileByNickInFlight.delete(key);
        });
    profileByNickInFlight.set(key, request);
    return request;
};

const isNumericId = (value) => Number.isFinite(Number(value));

const normalizeMember = (member) => {
    if (member === null || member === undefined) {
        return null;
    }
    if (typeof member === 'string' || typeof member === 'number') {
        return { id: member, nick: null };
    }
    const id = member.userId || member.id || member.uid || member.accountId || member.profileId;
    const nick = member.nick || member.nickname || member.username || member.name || member.title;
    const role = member.role || member.position || member.memberRole;
    return (id || nick) ? { id, nick, role } : null;
};

const mergeMembers = (list) => {
    const map = new Map();
    list.forEach((member) => {
        if (!member) {
            return;
        }
        const key = member.id !== null && member.id !== undefined
            ? `id:${member.id}`
            : (member.nick ? `nick:${member.nick.toLowerCase()}` : null);
        if (!key) {
            return;
        }
        const existing = map.get(key) || {};
        map.set(key, {
            ...existing,
            ...member,
            nick: member.nick || existing.nick || null,
        });
    });
    return Array.from(map.values());
};

const extractMembersFromTeam = (team) => {
    if (!team) {
        return [];
    }
    const members = [];
    const owner = team.owner || team.ownerProfile || team.ownerUser;
    const ownerId = team.ownerId || team.owner_id || team.ownerUserId || team.owner_user_id;
    if (owner || ownerId) {
        members.push(owner || { id: ownerId });
    }
    const sources = [
        team.members,
        team.users,
        team.participants,
        team.teamMembers,
        team.memberships,
    ];
    sources.forEach((source) => {
        if (Array.isArray(source)) {
            members.push(...source);
        }
    });
    return members;
};

const mapMangaEntries = (entries) => (
    (entries || [])
        .map((entry) => entry?.manga || entry?.mangaEntity || entry?.mangaItem || entry)
        .filter(Boolean)
);

const normalizeMangaPage = (response, requestedPage) => {
    if (!response) {
        return { items: [], last: true, page: requestedPage };
    }
    const payload = response?.data ?? response;
    if (Array.isArray(payload)) {
        return { items: payload, last: true, page: requestedPage };
    }
    const items = unwrapList(response);
    const metaCandidate = (payload
        && !Array.isArray(payload)
        && payload.data
        && !Array.isArray(payload.data)
        && typeof payload.data === 'object'
        && (
            payload.data.page !== undefined
            || payload.data.number !== undefined
            || payload.data.totalPages !== undefined
            || payload.data.total_pages !== undefined
            || payload.data.last !== undefined
        )) ? payload.data : payload;
    const pageValue = Number(metaCandidate?.number ?? metaCandidate?.page ?? requestedPage ?? 0);
    const totalPages = Number(metaCandidate?.totalPages ?? metaCandidate?.total_pages ?? 0);
    const last = metaCandidate?.last
        ?? (totalPages ? pageValue >= totalPages - 1 : items.length < PAGE_SIZE);
    return { items, last, page: pageValue };
};

const hydrateMembers = async (list) => {
    const candidates = list.filter((member) => member?.nick);
    if (candidates.length === 0) {
        return list;
    }
    const results = await Promise.allSettled(
        candidates.map((member) => fetchProfileByNick(member.nick))
    );
    const lookup = new Map();
    results.forEach((result, index) => {
        if (result.status !== 'fulfilled') {
            return;
        }
        const payload = result.value?.data || result.value;
        const profile = payload?.data || payload;
        if (!profile) {
            return;
        }
        const resolvedId = profile?.id || candidates[index]?.id;
        const nick = profile?.nickname || profile?.nick || candidates[index]?.nick;
        if (!nick) {
            return;
        }
        const avatarUrl = getAvatarUrl(resolvedId);
        lookup.set(nick.toLowerCase(), {
            id: resolvedId || candidates[index]?.id,
            nick,
            avatarUrl,
            profile,
        });
    });
    return list.map((member) => {
        const key = member?.nick ? member.nick.toLowerCase() : null;
        if (!key) {
            return member;
        }
        const enriched = lookup.get(key);
        if (!enriched) {
            return member;
        }
        return {
            ...member,
            id: enriched.id || member.id,
            nick: member.nick || enriched.nick || null,
            avatarUrl: member.avatarUrl || enriched.avatarUrl || null,
            profile: member.profile || enriched.profile || null,
        };
    });
};

const getTeamName = (team, fallbackId) => (
    team?.name || team?.title || team?.teamName || (fallbackId ? `Команда #${fallbackId}` : 'Команда')
);

const getTeamDescription = (team) => (
    team?.description || team?.about || team?.summary || ''
);

const MangaTeamPage = () => {
    const { teamId: teamIdParam } = useParams();
    const scrolled = useScrolled();
    const [team, setTeam] = useState(null);
    const [teamLoading, setTeamLoading] = useState(false);
    const [teamError, setTeamError] = useState(null);
    const [logoUrl, setLogoUrl] = useState(null);
    const [logoLoaded, setLogoLoaded] = useState(false);
    const [members, setMembers] = useState([]);
    const [membersLoading, setMembersLoading] = useState(false);
    const [membersError, setMembersError] = useState(null);
    const [mangas, setMangas] = useState([]);
    const [mangasLoading, setMangasLoading] = useState(false);
    const [mangasError, setMangasError] = useState(null);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const scrollTimeoutRef = useRef(null);
    const pagesRequestedRef = useRef(new Set());
    const requestKeyRef = useRef('');
    const inflightCountRef = useRef(0);
    const pagingLockRef = useRef(false);

    const isAllowed = MangaApiService.isAllowedDomain();
    const resolvedTeamId = teamIdParam || getTeamId(team);

    const startMangasLoading = () => {
        inflightCountRef.current += 1;
        setMangasLoading(true);
    };

    const finishMangasLoading = () => {
        inflightCountRef.current = Math.max(0, inflightCountRef.current - 1);
        if (inflightCountRef.current === 0) {
            setMangasLoading(false);
        }
    };

    const releasePagingLock = () => {
        pagingLockRef.current = false;
    };

    const isMangasBusy = () => inflightCountRef.current > 0 || mangasLoading;

    const getScrollMetrics = () => {
        const root = document.documentElement;
        if (!root) {
            return null;
        }
        const scrollHeight = root.scrollHeight || root.offsetHeight;
        const viewportHeight = window.innerHeight || 0;
        const position = window.pageYOffset + viewportHeight;
        return { scrollHeight, viewportHeight, position };
    };

    const isNearBottom = () => {
        const metrics = getScrollMetrics();
        if (!metrics) {
            return false;
        }
        return metrics.position >= metrics.scrollHeight - SCROLL_BUFFER_PX;
    };

    const isScrollShort = () => {
        const metrics = getScrollMetrics();
        if (!metrics) {
            return false;
        }
        return metrics.scrollHeight <= metrics.viewportHeight + SCROLL_BUFFER_PX;
    };

    const queueNextPage = () => {
        if (pagingLockRef.current || !hasMore || isMangasBusy()) {
            return;
        }
        pagingLockRef.current = true;
        setPage((prevPage) => prevPage + 1);
    };

    useEffect(() => {
        document.title = 'animix - Команда';
        const handleScroll = () => {
            if (scrollTimeoutRef.current) {
                return;
            }
            scrollTimeoutRef.current = setTimeout(() => {
                if (!isAllowed || !teamIdParam) {
                    scrollTimeoutRef.current = null;
                    return;
                }
                if (hasMore && !isMangasBusy() && isNearBottom()) {
                    queueNextPage();
                }
                scrollTimeoutRef.current = null;
            }, 150);
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', handleScroll);
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
                scrollTimeoutRef.current = null;
            }
        };
    }, [hasMore, isAllowed, mangasLoading, teamIdParam]);

    useEffect(() => {
        if (!isAllowed || !teamIdParam) {
            return;
        }
        let isActive = true;
        const loadTeam = async () => {
            setTeamLoading(true);
            setTeamError(null);
            try {
                const response = await MangaApiService.getTeam(teamIdParam);
                const payload = unwrapResponse(response);
                const teamData = payload?.team || payload?.data || payload;
                if (teamData && (teamData.id || teamData.name || teamData.title || teamData.teamId)) {
                    if (isActive) {
                        setTeam(teamData);
                        const name = getTeamName(teamData, teamIdParam);
                        if (name) {
                            document.title = `animix - ${name}`;
                        }
                    }
                } else if (isActive) {
                    setTeam(null);
                    setTeamError('Команда не найдена.');
                }
            } catch (err) {
                if (isActive) {
                    setTeam(null);
                    setTeamError(getMangaErrorMessage(err, 'Не удалось загрузить команду.'));
                }
            } finally {
                if (isActive) {
                    setTeamLoading(false);
                }
            }
        };
        loadTeam();
        return () => {
            isActive = false;
        };
    }, [isAllowed, teamIdParam]);

    useEffect(() => {
        let isActive = true;
        const resolveLogo = async () => {
            if (!team) {
                if (isActive) {
                    setLogoUrl(null);
                }
                return;
            }
            const logo = team.logo || team.logoFile || team.logo_data || {};
            const fileId = team.logoFileId || team.logo_file_id || logo.fileId || logo.id || logo.file_id;
            const bucket = team.logoStorageBucket || team.logo_storage_bucket || logo.bucket || logo.storageBucket || logo.storage_bucket;
            if (!fileId || !bucket) {
                if (isActive) {
                    setLogoUrl(null);
                }
                return;
            }
            const direct = MangaApiService.resolveFileUrl({
                fileId,
                bucket,
                fileUrl: team.logoFileUrl || team.logo_file_url || logo.fileUrl || logo.file_url,
                url: team.logoUrl || logo.url,
                publicBaseUrl: logo.publicBaseUrl || logo.public_base_url,
            }, bucket);
            if (direct) {
                if (isActive) {
                    setLogoUrl(direct);
                }
                return;
            }
            try {
                const resolved = await MangaApiService.resolveFileUrlAsync({ fileId, bucket }, bucket);
                if (isActive) {
                    setLogoUrl(resolved);
                }
            } catch (error) {
                if (isActive) {
                    setLogoUrl(null);
                }
            }
        };
        resolveLogo();
        return () => {
            isActive = false;
        };
    }, [team]);

    useEffect(() => {
        setLogoLoaded(false);
    }, [logoUrl]);

    useEffect(() => {
        if (!isAllowed || !teamIdParam) {
            return;
        }
        let isActive = true;
        const loadMembers = async () => {
            setMembersLoading(true);
            setMembersError(null);
            try {
                const fromTeam = extractMembersFromTeam(team);
                let normalized = mergeMembers(fromTeam.map(normalizeMember).filter(Boolean));
                if (normalized.length === 0) {
                    const response = await MangaApiService.getTeamMembers(teamIdParam);
                    const list = unwrapList(response);
                    normalized = mergeMembers(list.map(normalizeMember).filter(Boolean));
                }
                const hydrated = await hydrateMembers(normalized);
                if (isActive) {
                    setMembers(hydrated);
                }
            } catch (err) {
                if (isActive) {
                    const status = err?.response?.status;
                    if (status === 404 || status === 405) {
                        setMembers([]);
                        setMembersError(null);
                    } else {
                        setMembersError(getMangaErrorMessage(err, 'Не удалось загрузить участников.'));
                        setMembers([]);
                    }
                }
            } finally {
                if (isActive) {
                    setMembersLoading(false);
                }
            }
        };
        loadMembers();
        return () => {
            isActive = false;
        };
    }, [isAllowed, teamIdParam, team]);

    const fetchMangas = async (pageNum = 0, requestKey = requestKeyRef.current) => {
        if (!isAllowed || !teamIdParam) {
            releasePagingLock();
            return;
        }
        const pageRequestKey = `${requestKey}|${pageNum}`;
        if (pagesRequestedRef.current.has(pageRequestKey)) {
            releasePagingLock();
            return;
        }
        pagesRequestedRef.current.add(pageRequestKey);

        startMangasLoading();
        setMangasError(null);
        try {
            const response = await MangaApiService.getTeamMangasPublic(teamIdParam, {
                page: pageNum,
                size: PAGE_SIZE,
            });
            const { items, last } = normalizeMangaPage(response, pageNum);
            if (requestKeyRef.current === requestKey) {
                const mapped = mapMangaEntries(items);
                setMangas((prev) => (pageNum === 0 ? mapped : [...prev, ...mapped]));
                setHasMore(!last);
            }
        } catch (err) {
            if (requestKeyRef.current === requestKey) {
                const status = err?.response?.status;
                if (status === 404) {
                    if (pageNum === 0) {
                        setMangas([]);
                    }
                    setMangasError(null);
                } else {
                    setMangasError(getMangaErrorMessage(err, 'Не удалось загрузить релизы команды.'));
                    if (pageNum === 0) {
                        setMangas([]);
                    }
                }
                setHasMore(false);
            }
        } finally {
            finishMangasLoading();
            releasePagingLock();
        }
    };

    useEffect(() => {
        if (!isAllowed || !teamIdParam) {
            setMangas([]);
            setHasMore(false);
            setMangasError(null);
            releasePagingLock();
            return;
        }
        const nextRequestKey = String(teamIdParam);
        requestKeyRef.current = nextRequestKey;
        pagesRequestedRef.current = new Set();
        setPage(0);
        setHasMore(true);
        setMangasError(null);
        setMangas([]);
        releasePagingLock();
        fetchMangas(0, nextRequestKey);
    }, [isAllowed, teamIdParam]);

    useEffect(() => {
        if (!isAllowed || !teamIdParam || page === 0) {
            return;
        }
        fetchMangas(page, requestKeyRef.current);
    }, [isAllowed, teamIdParam, page]);

    useEffect(() => {
        if (!isAllowed || !teamIdParam || !hasMore) {
            return;
        }
        if (isMangasBusy()) {
            return;
        }
        if (isNearBottom() || isScrollShort()) {
            queueNextPage();
        }
    }, [isAllowed, teamIdParam, hasMore, mangas.length, mangasLoading]);

    if (!isAllowed) {
        return (
            <PageShell>
                <MangaAccessNotice />
            </PageShell>
        );
    }

    const teamName = getTeamName(team, resolvedTeamId);
    const teamDescription = getTeamDescription(team);
    const rawDonationLinks = resolveDonationLinks(team);
    const donationLinks = Array.isArray(rawDonationLinks)
        ? rawDonationLinks.filter(Boolean)
        : (rawDonationLinks ? [rawDonationLinks] : []);
    const teamStatus = team?.status || team?.state || team?.moderationStatus;
    const teamStatusLabel = translateStatus(teamStatus);

    return (
        <div className="app">
            <Header scrolled={scrolled} />
            <div className="manga-team-backline">
                <Link className="manga-team-back" to="/manga">
                    <img src={backIcon} alt="" aria-hidden="true" />
                    Каталог манги
                </Link>
            </div>
            <div className="anime-info-content manga-info-content manga-team-hero">
                {logoUrl ? (
                    <img
                        src={logoUrl}
                        alt={teamName}
                        className={`blud-back ${logoLoaded ? 'loaded' : ''}`}
                        onLoad={() => setLogoLoaded(true)}
                    />
                ) : null}
                <div className="anime-info-container">
                    <div className="manga-team-hero-card">
                        {logoUrl ? (
                            <img src={logoUrl} alt={teamName} />
                        ) : (
                            <div className="manga-team-hero-placeholder">
                                <img src={teamIcon} alt="" aria-hidden="true" />
                            </div>
                        )}
                    </div>
                    <div className="anime-info-div">
                        <div className="center-content manga-tags">
                            {teamStatus ? <span className="manga-tag">{teamStatusLabel}</span> : null}
                            {resolvedTeamId ? <span className="manga-tag">ID {resolvedTeamId}</span> : null}
                        </div>
                        <h1 className="anime-name">{teamName}</h1>
                        <p className="anime-text">
                            {teamDescription || 'Описание команды пока не заполнено.'}
                        </p>
                        {teamLoading ? <div className="loader" /> : null}
                        {teamError ? <div className="manga-inline-warning">{teamError}</div> : null}
                    </div>
                </div>
            </div>

            <div className="anime-half-con manga-team-columns">
                <div className="anime-half-desc">
                    <h1>Участники</h1>
                    {donationLinks.length > 0 ? (
                        <div className="manga-team-donations">
                            <div className="manga-studio-links">
                                <div className="manga-studio-links-title">Донаты</div>
                                {donationLinks.map((link) => (
                                    <a
                                        key={link}
                                        href={link}
                                        rel="noreferrer"
                                        onClick={(event) => {
                                            event.preventDefault();
                                            ExternalLinkService.openExternal(link);
                                        }}
                                    >
                                        {link}
                                    </a>
                                ))}
                            </div>
                        </div>
                    ) : null}
                    {membersLoading ? <div className="loader" /> : null}
                    {membersError ? <div className="manga-inline-warning">{membersError}</div> : null}
                    {members.length > 0 ? (
                        <div className="manga-team-members manga-team-members--public">
                            {members.map((member) => {
                                const key = member.id || member.nick;
                                const profileNick = member.nick
                                    || member.profile?.nickname
                                    || member.profile?.nick
                                    || member.profile?.username
                                    || member.profile?.name;
                                const displayName = profileNick || 'Ник не указан';
                                const accountPayload = member.profile
                                    || (member.id ? { id: member.id, nickname: displayName } : null);
                                const cardContent = (
                                    <>
                                        <div className="manga-team-member-avatar">
                                            {accountPayload ? (
                                                <ProfilePic isBig={false} account={accountPayload} />
                                            ) : (
                                                <div className="manga-comment-avatar" aria-hidden="true" />
                                            )}
                                        </div>
                                        <div className="manga-team-member-body">
                                            <div className="manga-team-member-name">{displayName}</div>
                                            {member.id ? (
                                                <div className="manga-team-member-id">ID: {member.id}</div>
                                            ) : null}
                                            {member.role ? (
                                                <div className="manga-team-member-role">{translateTeamRole(member.role)}</div>
                                            ) : null}
                                        </div>
                                    </>
                                );
                                return profileNick ? (
                                    <Link
                                        key={key}
                                        className="manga-team-member manga-team-member--public manga-team-member-link"
                                        to={`/profile/${encodeURIComponent(profileNick)}`}
                                    >
                                        {cardContent}
                                    </Link>
                                ) : (
                                    <div key={key} className="manga-team-member manga-team-member--public">
                                        {cardContent}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="manga-empty">Участники пока не указаны.</div>
                    )}
                </div>

                <div className="anime-half">
                    <h1>Релизы команды</h1>
                    {mangasError ? <div className="manga-inline-warning">{mangasError}</div> : null}
                    <div className="anime-container">
                        {mangas.length > 0 ? (
                            mangas.map((manga) => (
                                <MangaCardComponent
                                    key={manga.slug || manga.id || manga.title}
                                    manga={manga}
                                    isInteractive
                                />
                            ))
                        ) : (
                            !mangasLoading ? <div className="manga-empty">Пока нет опубликованных тайтлов.</div> : null
                        )}
                    </div>
                    {mangasLoading ? <div className="loader" /> : null}
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default MangaTeamPage;
